import { useUpdateLockPIN } from '@akin-home/lock-screen/react';
import produce from 'immer';
import React, { FC, useEffect, useReducer } from 'react';
import { LockScreenOverlay } from '../LockScreenOverlay';
import LockScreenView from '../LockScreenView';
import { getLockScreenTitle } from '../LockScreenView/getLockScreenTitle';

interface Props {
  user_id?: string;
  onClose: () => void;
}

interface State {
  /** The input in the lock screen */
  input: string;
  /** Whether the lock screen is being shown */
  visible?: boolean;
  /** Error displaying on the lock screen */
  error?: string;
  user_id?: string;
  initial_pin?: string;
  done?: boolean;
}

enum ActionType {
  OPEN,
  /** Add a number to the input */
  ADD_NUMBER,
  /** Deletes a number form the input */
  DELETE_NUMBER,
  /** Reset everything */
  RESET,
}

type Action =
  | { type: ActionType.OPEN; payload: { user_id: string } }
  | { type: ActionType.ADD_NUMBER; payload: { number: number } }
  | { type: ActionType.DELETE_NUMBER }
  | { type: ActionType.RESET };

const reducer = produce((state: State, action: Action) => {
  switch (action.type) {
    case ActionType.OPEN: {
      state = {
        ...default_state,
        ...action.payload,
        visible: true,
      };
      break;
    }

    case ActionType.ADD_NUMBER: {
      const input = `${state.input}${action.payload.number}`;
      const filled_pin = input.length === 4;

      if (!filled_pin) {
        state.visible = true;
        state.input = input;
        break;
      }
      if (!state.initial_pin) {
        state.input = '';
        state.initial_pin = input;
        break;
      } else if (input === state.initial_pin) {
        state.input = input;
        state.done = true;
        break;
      } else {
        state.input = '';
        state.error = 'PIN did not match, try again.';
        delete state.initial_pin;
        break;
      }
    }

    case ActionType.DELETE_NUMBER: {
      const { input } = state;
      state.input = input.length === 0 ? input : input.slice(0, -1);
      break;
    }

    case ActionType.RESET: {
      state = default_state;
    }
  }
});

const default_state: State = { input: '' };

const SetProfilePinScreen: FC<Props> = (props) => {
  const [state, dispatch] = useReducer(reducer, default_state);
  const setLockPIN = useUpdateLockPIN();

  const onPressNumber = (number: number) =>
    dispatch({ type: ActionType.ADD_NUMBER, payload: { number } });

  const onPressDelete = () => dispatch({ type: ActionType.DELETE_NUMBER });

  const onPressClose = () => {
    dispatch({
      type: ActionType.RESET,
    });

    props.onClose();
  };

  const title = state.initial_pin
    ? 'Enter your PIN again to confirm'
    : undefined;

  useEffect(() => {
    if (props.user_id && props.user_id !== state.user_id) {
      dispatch({ type: ActionType.OPEN, payload: { user_id: props.user_id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user_id]);

  useEffect(() => {
    if (state.done && state.input && props.user_id) {
      // Set the users PIN
      setLockPIN.mutateAsync({
        user_id: props.user_id,
        pin: state.input,
      });

      dispatch({ type: ActionType.RESET });

      props.onClose();
    }
  }, [state.done]);

  return (
    <LockScreenOverlay visible={!!props.user_id}>
      <LockScreenView
        title={title || getLockScreenTitle(state)}
        input={state.input}
        error={state.error}
        onInputNumber={onPressNumber}
        onClose={onPressClose}
        onRemoveNumber={onPressDelete}
      />
    </LockScreenOverlay>
  );
};

export default SetProfilePinScreen;
